import { Button, clearNullDataDeep, defaultTo, ifElse, messageSuccess, Spin } from 'aa_common/front-end'
import { BottomAction, ErrorsList } from 'aa_common/front-end/components'
import { Table } from 'antd'
import { hasRetroactiveAssets } from 'common/helpers'
import { getNs } from 'common/i18n-config'
import { Asset } from 'common/models'
import { FixedAssetEventsService } from 'common/open-api'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { Pagination } from 'components/molecules'
import { importBulkChangeAction, importBulkChangeSlice } from 'components/organisms/assets/AssetImportForm/store'
import useRetroactiveAssetModal from 'components/templates/modal/RetroactiveAssetModal'
import { useLongPolling } from 'hooks/useLongPolling'
import { isEmpty, isEqual } from 'lodash'
import { IMPORT_EXPORT_EVENT_TYPES } from 'pages/assets/AssetListPage/import-export-event-type-selector'
import { importExportEventTypeSelectorSlice } from 'pages/assets/AssetListPage/store'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { selectCurrentMonthlyClosing } from 'store/accounting/monthlyClosing/selectors'
import { CLEANUP_ASSET_IMPORT, CREATE_BULK_ASSET_LIST_REQUEST, setEditAssetItem } from 'store/assetImport/actions'
import { selectAssetImportListData, selectAssetImportListErrors } from 'store/assetImport/selectors'

import { getColumns, getDetailError, parseDataForTable } from './helper'
import { useImportTables } from './hooks'
import { NoteContainer, StyledPaginationWrapper, Wrapper } from './styles'

const { t, useNsReady } = getNs(['common', 'components/organisms/settings/setting-import-result-list'])

const SettingImportResultList: React.FC = () => {
  useNsReady()
  const dispatch = useDispatch()
  const {
    location: { pathname, key: locationKey },
    push,
    goBack,
  } = useHistory()
  const DEFAULT_PAGE_SIZE = 50

  const importList = useSelector<any, Asset[]>(selectAssetImportListData, isEqual)
  const errorList = useSelector(selectAssetImportListErrors, isEqual)
  const { data: currentMonthlyClosing } = useSelector(selectCurrentMonthlyClosing, isEqual)
  const store = importBulkChangeSlice.useSlice()
  const { importEventType } = importExportEventTypeSelectorSlice.useSlice()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)

  const { importBulkChangeType, data, errorBiids, errors } = store

  const bulkChangeData = useImportTables(importBulkChangeType, data?.events)

  const confirmModal = useRetroactiveAssetModal()

  const handleRowClick = useCallback(
    (record: any, index: number) => {
      const selectedItem = importList.slice((currentPage - 1) * DEFAULT_PAGE_SIZE, currentPage * DEFAULT_PAGE_SIZE)[
        index
      ]
      dispatch(setEditAssetItem(clearNullDataDeep(selectedItem)))
    },
    [importList, currentPage]
  )

  const onError = (errorBiids: string[]) => {
    importBulkChangeAction.setErrorBiids(errorBiids)
  }

  const onSuccess = () => {
    push('/assets')
    messageSuccess(t('messages.imported_bulk_change_success'))
  }

  const importCsvBulkchange = useLongPolling('importCsvBulkchange', { onError, onSuccess })
  const bulkChangeEventPolling = async () => {
    try {
      const res: any = await FixedAssetEventsService.eventsCreate({
        file_url: data?.upload_url,
        event_type_code: importBulkChangeType,
      })
      importCsvBulkchange.setJobId(res?.data?.id)
    } catch (e) {
      /* empty */
    }
  }

  const handleSubmit = () => {
    importBulkChangeAction.clearErrors()
    const createBulk = () => {
      importBulkChangeType ? bulkChangeEventPolling() : dispatch({ type: CREATE_BULK_ASSET_LIST_REQUEST })
    }

    if (!importBulkChangeType && hasRetroactiveAssets(importList, currentMonthlyClosing)) {
      // Open the retroactive asset warning modal
      confirmModal.open({
        onOKClick: () => createBulk(),
      })
    } else {
      // No retroactive asset in the list
      createBulk()
    }
  }

  const isEmptyImportList = isEmpty(importList)

  useEffect(() => {
    if (isEmptyImportList && !importBulkChangeType) {
      const previousUrl = pathname.substring(0, pathname.lastIndexOf('/'))
      // if can't go back we will using the previousUrl, eg: /assets/import/results -> /assets/import
      locationKey ? goBack() : push(previousUrl)
    }
    return function cleanup() {
      dispatch({ type: CLEANUP_ASSET_IMPORT })
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    return () => {
      importBulkChangeAction.reset()
    }
  }, [])

  const { errorDetails, errorLines } = getDetailError(errors)

  const bulkChangeImportDataSource = useMemo(() => {
    return bulkChangeData?.dataTable?.dataSource?.map((item: any, index: number) => ({
      ...item,
      isError: errorBiids.includes(item.biid) || errorLines.includes(index + 1),
    }))
  }, [errorBiids, errorLines])

  const isImportForBulkChange = useMemo(() => {
    return importEventType !== null && importEventType !== IMPORT_EXPORT_EVENT_TYPES.FIXED_ASSET_LEDGER
  }, [importEventType])

  const paginationConfig = useMemo(() => {
    const total = ifElse(
      isImportForBulkChange,
      bulkChangeImportDataSource?.length,
      parseDataForTable(importList)?.length
    )

    const totalPages = Math.ceil(total / DEFAULT_PAGE_SIZE)

    return { total, totalPages }
  }, [isImportForBulkChange, bulkChangeImportDataSource, importList])

  const handlePaginationChange = (page: number) => {
    setLoading(true)

    setTimeout(() => {
      setCurrentPage(page)
      setLoading(false)
    }, 200)
  }

  const hasError: boolean = !!errorList?.length || !!errorDetails.length
  const { isEnable: isEnableReduction } = useCheckFlag(FeatureFlagNames.FixedAssetReduction)
  const isStickColumn = IMPORT_EXPORT_EVENT_TYPES.SALE || IMPORT_EXPORT_EVENT_TYPES.RETIREMENT

  const tableDataSource = useMemo(() => {
    const dataSource = ifElse(isImportForBulkChange, bulkChangeImportDataSource, parseDataForTable(importList))

    return dataSource?.slice((currentPage - 1) * DEFAULT_PAGE_SIZE, currentPage * DEFAULT_PAGE_SIZE)
  }, [currentPage, isImportForBulkChange, bulkChangeImportDataSource, importList])

  return (
    <Spin loading={importCsvBulkchange.loading}>
      <Wrapper>
        <NoteContainer>{t(isImportForBulkChange ? 'bulk_change_import_notice_message' : 'notice_msg')}</NoteContainer>
        {hasError ? (
          <ErrorsList
            title={t('error_header')}
            errors={errorList || errorDetails}
            style={{ marginTop: 10, maxHeight: 150, overflowY: 'auto' }}
          />
        ) : null}
        <StyledPaginationWrapper>
          <Pagination
            name="fixed-asset-import-preview"
            totalPages={paginationConfig.totalPages}
            totalRecords={paginationConfig.total}
            value={currentPage}
            onPageChanged={handlePaginationChange}
          />
        </StyledPaginationWrapper>
        <div>
          {isImportForBulkChange ? (
            <Table
              loading={loading}
              style={ifElse(isStickColumn, { marginTop: 10, width: 'calc(100vw - 180px)' }, { marginTop: 10 })}
              columns={defaultTo(bulkChangeData?.dataTable?.columns, [])}
              dataSource={tableDataSource}
              pagination={false}
              scroll={ifElse(isStickColumn, { x: true }, {})}
              onRow={(record: any) => ({
                className: record.isError ? 'error' : '',
              })}
            />
          ) : (
            <Table
              loading={loading}
              style={{ marginTop: 10 }}
              columns={getColumns(isEnableReduction)}
              dataSource={tableDataSource}
              pagination={false}
              onRow={(record: any, rowIndex: any) => {
                return {
                  className: ifElse(record.is_error, 'error', ''),
                  onClick: () => {
                    handleRowClick(record, rowIndex)
                  }, // click row
                }
              }}
            />
          )}
        </div>
        <BottomAction id="submit-bottom" style={{ justifyContent: 'flex-end', background: 'rgba(255, 255, 255, 0.8)' }}>
          <Button disabled={hasError} data-testid="submit-import-btn" style={{ height: 42 }} onClick={handleSubmit}>
            {t('actions.register')}
          </Button>
        </BottomAction>
      </Wrapper>
    </Spin>
  )
}

export default SettingImportResultList
